/**
 * Ktoré vlákno patrí ktorému zákazníkovi.
 *
 * Zákazník je zapísaný na výstupe zákazníckeho splittra. Keď má ten výstup
 * vyplnený kábel a vlákno, jeho signál ide ďalej — cez zvary a priechody môže
 * skončiť na úplne inom stĺpiku. Napríklad Eva Maďarová visí na splittri na
 * Stlpik_53, ale fyzicky je pripojená až na Stlpik_56:
 *
 *   splitter@Stlpik_53 výstup 1 → kábel 1416 vl 1
 *   zvar@Stlpik_53:  1416 vl 1 → 776 vl 1   (modré)
 *   zvar@Stlpik_56:  776 vl 1  → 1417 vl 1
 *
 * V dátach je to celé, ale na Stlpik_56 to nikde nevidno — je tam len zvar bez
 * mena. Tento index prejde vlákno dopredu a označí každý jeho úsek menom
 * zákazníka, aby sa dal v kazete zobraziť na oboch koncoch.
 */
import { Pole } from '../types';

export interface ZakaznikNaVlakne {
  meno: string;
  /** stĺpik, na ktorého splittri zákazník visí */
  zoStlpika: string;
  splitterTyp: string | null;
  vystup: number;
}

/** kľúč je `cableId:fiber` */
export type IndexVlakien = Map<string, ZakaznikNaVlakne>;

const kluc = (cableId: number, fiber: number) => `${cableId}:${fiber}`;

/**
 * Prejde vlákno dopredu cez zvary, priechody aj odbočky a vráti všetky úseky,
 * ktorými vedie. Nesleduje smer — zvar je obojsmerný a nás zaujíma len to,
 * ktoré úseky patria tomu istému vláknu.
 */
const prejdiVlakno = (
  poles: Pole[],
  cableId: number,
  fiber: number,
  limit = 40,
): { cableId: number; fiber: number }[] => {
  const najdene: { cableId: number; fiber: number }[] = [];
  const videne = new Set<string>();
  const fronta = [{ cableId, fiber }];

  while (fronta.length && najdene.length < limit) {
    const u = fronta.shift()!;
    const k = kluc(u.cableId, u.fiber);
    if (videne.has(k)) continue;
    videne.add(k);
    najdene.push(u);

    for (const p of poles) {
      const kaz: any = p.cassette;
      if (!kaz) continue;

      let zvarNaTomtoStlpe = false;
      for (const z of (kaz.splices || []) as any[]) {
        if (z.from_cable_id === u.cableId && z.from_fiber === u.fiber && z.to_cable_id) {
          fronta.push({ cableId: z.to_cable_id, fiber: z.to_fiber });
          zvarNaTomtoStlpe = true;
        }
        if (z.to_cable_id === u.cableId && z.to_fiber === u.fiber && z.from_cable_id) {
          fronta.push({ cableId: z.from_cable_id, fiber: z.from_fiber });
          zvarNaTomtoStlpe = true;
        }
      }

      // Keď je vlákno na tomto stĺpe zvarené, priechod sa už nesleduje —
      // zvar ho „spotreboval". Bez toho by sa zákazník pripísal aj káblu,
      // ktorý ide ďalej do siete: na Stlpik_56 vedie priechod 776 → 792
      // to isté vlákno 1, ale Evin signál tam končí zvarom do kazety.
      if (zvarNaTomtoStlpe) continue;

      for (const pt of (kaz.pass_through_cables || []) as any[]) {
        // Priechod vedie vlákno pod tým istým číslom na druhý kábel
        if (pt.from_cable_id === u.cableId && pt.to_cable_id) {
          fronta.push({ cableId: pt.to_cable_id, fiber: u.fiber });
        }
        if (pt.to_cable_id === u.cableId && pt.from_cable_id) {
          fronta.push({ cableId: pt.from_cable_id, fiber: u.fiber });
        }
        for (const vl of (pt.fibers || []) as any[]) {
          if (!vl?.is_tapped || !vl.tap_to_cable_id) continue;
          if (pt.from_cable_id === u.cableId && vl.fiber_number === u.fiber) {
            fronta.push({ cableId: vl.tap_to_cable_id, fiber: vl.tap_to_fiber ?? u.fiber });
          }
          if (vl.tap_to_cable_id === u.cableId && (vl.tap_to_fiber ?? vl.fiber_number) === u.fiber) {
            fronta.push({ cableId: pt.from_cable_id, fiber: vl.fiber_number });
          }
        }
      }
    }
  }

  return najdene;
};

/**
 * Index všetkých vlákien, ktoré patria nejakému zákazníkovi.
 *
 * Počíta sa raz pre celý projekt; pri 90 zákazníkoch v Ohaji je to zlomok
 * sekundy a výsledok sa dá držať, kým sa nezmenia stĺpy.
 */
export const indexZakaznickychVlakien = (poles: Pole[]): IndexVlakien => {
  const index: IndexVlakien = new Map();

  poles.forEach((p) => {
    ((p.cassette?.splitters || []) as any[]).forEach((sp) => {
      if (sp?.is_customer_splitter !== true) return;
      (sp.outputs || []).forEach((o: any, i: number) => {
        if (!o?.customer_name || !o.cable_id || !o.fiber) return;
        const zaznam: ZakaznikNaVlakne = {
          meno: String(o.customer_name).trim(),
          zoStlpika: p.name,
          splitterTyp: sp.type || null,
          vystup: i + 1,
        };
        prejdiVlakno(poles, o.cable_id, o.fiber).forEach((u) => {
          const k = kluc(u.cableId, u.fiber);
          if (!index.has(k)) index.set(k, zaznam);
        });
      });
    });
  });

  return index;
};

export const zakaznikNaVlakne = (
  index: IndexVlakien,
  cableId?: number | null,
  fiber?: number | null,
): ZakaznikNaVlakne | null => {
  if (!cableId || !fiber) return null;
  return index.get(kluc(cableId, fiber)) || null;
};


/** Port zákazníckeho splittra, ktorý kŕmi dané vlákno. */
export interface PortNaVlakne {
  poleId: number;
  poleName: string;
  splitterIndex: number;
  vystup: number;
  splitterTyp: string | null;
  /** meno zákazníka, ak už na porte niekto je */
  zakaznik: string | null;
}

/**
 * Index vlákien na porty zákazníckych splitterov — aj na tie VOĽNÉ.
 *
 * Kým `indexZakaznickychVlakien` hovorí „na tomto vlákne visí Eva",
 * tento hovorí „toto vlákno kŕmi port 1 splittra na Stlpik_53, ktorý je
 * voľný". Bez toho by sa pri prípojke na inom stĺpiku nedalo zistiť,
 * kam zákazníka vlastne pripojiť.
 */
export const indexPortovSplitterov = (poles: Pole[]): Map<string, PortNaVlakne> => {
  const index = new Map<string, PortNaVlakne>();

  poles.forEach((p) => {
    ((p.cassette?.splitters || []) as any[]).forEach((sp, si) => {
      if (sp?.is_customer_splitter !== true) return;
      (sp.outputs || []).forEach((o: any, i: number) => {
        if (!o?.cable_id || !o?.fiber) return;
        const zaznam: PortNaVlakne = {
          poleId: p.id,
          poleName: p.name,
          splitterIndex: si,
          vystup: i + 1,
          splitterTyp: sp.type || null,
          zakaznik: o.customer_name ? String(o.customer_name).trim() : null,
        };
        prejdiVlakno(poles, o.cable_id, o.fiber).forEach((u) => {
          const k = kluc(u.cableId, u.fiber);
          if (!index.has(k)) index.set(k, zaznam);
        });
      });
    });
  });

  return index;
};

export const portNaVlakne = (
  index: Map<string, PortNaVlakne>,
  cableId?: number | null,
  fiber?: number | null,
): PortNaVlakne | null => {
  if (!cableId || !fiber) return null;
  return index.get(kluc(cableId, fiber)) || null;
};
